import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FirstDataRenderedEvent, GridApi, GridOptions, GridReadyEvent, RowClassParams, RowDataUpdatedEvent } from 'ag-grid-community';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { SelectedDateRange } from '../../cxone-etl-recalculation/cxone-etl-recalculation-modal/cxone-etl-data-recalculation.model';
import { CxoneEtlRecalculationService } from '../../cxone-etl-recalculation/cxone-etl-recalculation-modal/cxone-etl-recalculation.service';
import { ETLMetricRecalculationGridObj, MetricTypes, SaveRecalculationDataObj } from '../../cxone-etl-recalculation/cxone-etl-recalculation-modal/cxone-etl-recalculation-modal.model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CustomNoRowsOverlayComponent } from '../../shared/custom-no-rows-overlay/custom-no-rows-overlay.component';
import { EtlCustomCellValueRendererComponent } from '../../shared/etl-custom-cell-value-renderer/etl-custom-cell-value-renderer.component';

@Component({
    selector: 'cv-manage-metrics-recalculation-modal',
    templateUrl: './manage-metrics-recalculation-modal.component.html',
    styleUrls: ['./manage-metrics-recalculation-modal.component.scss']
})
export class ManageMetricsRecalculationModalComponent implements OnInit {
    public searchPlaceholderText = '';
    public searchInput = '';
    public metricsGridOptions: GridOptions;
    public isLocalizationSectionLoaded: boolean;
    public showSpinner: boolean;
    public gridApi: GridApi;
    public headerTitle: string;
    public isDefinitionSelected = false;
    public metricsLabel: string;
    public recalculateLabel: string;
    public cancelLabel: string;
    public dateRangeObj: SelectedDateRange;
    public dateRangeInfoMsg = '';
    public customDateRange: { maxDate: Date };
    public startDate: string;
    public endDate: string;
    public gridDataMetrics: ETLMetricRecalculationGridObj[] = [];
    public selectedMetricDefList: string[] = [];
    public selectedMetricId: number;
    public isRecalculationContextOption: boolean;
    public hideMetricsUpdatedToaster: boolean;
    public inProgressMetricList: string[];
    public inProgressMetricsInfo: string;
    public entitiesMaxSelectionLimit = 10;
    public showWarningMsg = false;
    public warningMessage: string;
    public isActionRunning = false;

    constructor(
        public dialogRef: DynamicDialogRef,
        public _dialogConfig: DynamicDialogConfig,
        private cvLocalizationService: CVLocalizationService,
        private cxoneEtlRecalculation: CxoneEtlRecalculationService,
        private datePipe: DatePipe,
        private toastrManager: ToastrManager,
        private agGridHelperService: AgGridHelperService
    ) {}

    async ngOnInit(): Promise<void> {
        this.showSpinner = true;
        await this.loadLocalization();
        this.isLocalizationSectionLoaded = true;
        const currentDate = new Date();
        this.dateRangeObj = {
            fromDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
            toDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
        };
        this.selectedMetricId = this._dialogConfig.data.metricsId;
        this.isRecalculationContextOption = this._dialogConfig.data.isRecalculationOption;
        this.hideMetricsUpdatedToaster = this._dialogConfig.data.hideMetricsUpdatedToaster;
        this.inProgressMetricList = this._dialogConfig.data.metricsInProgress;
        this.setSelectableDateRange();
        this.setGridOptions();
    }

    public async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('Manage_Metrics_Recalculation');
        }
        catch {
            console.log('Failed to load localization for Manage_Metrics_Recalculation');
        }
        finally {
            this.searchPlaceholderText = this.getLocalizationString("Search", "Search...");
            this.recalculateLabel = this.getLocalizationString("Recalculate", "Recalculate");
            this.cancelLabel = this.getLocalizationString("Cancel", "Cancel");
            this.headerTitle = this.getLocalizationString("Recalculate_Metrics", "Recalculate Metrics");
            this.dateRangeInfoMsg = this.getLocalizationString("Date_Range_Info", "Recalculation starts on the 1st of each month. Selection covers entire month and can be for more than a month.");
            this.inProgressMetricsInfo = this.getLocalizationString("InProgress_Metric_Info", "The Metrics, which are presently being recalculated, are not available for selection.");
            this.warningMessage = this.getLocalizationString("Warning_Message", "A maximum of 10 entities can be recalculated at a time.");
        }
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("Manage_Metrics_Recalculation", key, defaultValue);
        return baseString;
    }

    public setGridOptions() {
        this.metricsGridOptions = {
            columnDefs: [
                {
                    field: 'MetricID',
                    colId: 'MetricID',
                    headerName: this.getLocalizationString("ID", "ID"),
                    checkboxSelection: true,
                    headerCheckboxSelection: false,
                    headerClass: ["checkBoxClass", "customFieldClass"],
                    cellClass: "customFieldCellClass",
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    width: 250,
                    resizable: true,
                    sortable: false,
                    suppressMovable: true,
                },
                {
                    field: 'MetricName',
                    colId: 'MetricName',
                    headerName: this.getLocalizationString("Metric_Name", "Metric Name"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    width: 250,
                    resizable: true,
                    sortable: true,
                    sort: 'asc',
                    suppressMovable: true
                },
                {
                    field: 'Group',
                    colId: 'Group',
                    headerName: this.getLocalizationString("Category", "Category"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    width: 250,
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
                {
                    field: 'Components',
                    colId: 'Components',
                    headerName: this.getLocalizationString("Reporting_Set", "Reporting Set"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: true,
                    },
                    width: 250,
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                }
            ],
            components: {
                CustomNoRowsOverlayComponent,
                cxoneETLCustomCellRenderer: EtlCustomCellValueRendererComponent
            },
            noRowsOverlayComponent: 'CustomNoRowsOverlayComponent',
            noRowsOverlayComponentParams: {
                noRowsOverlayTextFunc: () => this.searchInput !== '' ?
                    this.getLocalizationString("No_Results_Found", "No results found")
                    : this.getLocalizationString("No_Metric_Added", "No metrics added yet"),
                noRowsOverlayIconPathFunc: () => {
                    const path = this.searchInput !== '' ?
                        '../App_Themes/CXone/icons/no-results.svg#no-results'
                        : '../App_Themes/CXone/icons/empty-state.svg#empty-state';
                    return path;
                }
            },
            context: this,
            rowHeight: 36,
            headerHeight: 44,
            animateRows: true,
            scrollbarWidth: 8,
            icons: {
                groupContracted:
                    `<div class="sortIcons">
            <svg class="icon-10">
                <use href="../App_Themes/CXone/icons/arrow-right.svg#arrow-right">
            </use></svg>
        </div>`,
                groupExpanded:
                    `<div class="sortIcons">
            <svg class="icon-10">
                <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
            </svg>
        </div>`
            },
            isExternalFilterPresent: () => true,
            doesExternalFilterPass: (node) => {
                const result = this.agGridHelperService.doesExternalFilterPassIncludeHidden(node, this.searchInput);
                return result;
            },
            onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
            onFirstDataRendered: (event: FirstDataRenderedEvent) => this.onFirstDataRendered(event),
            onRowDataUpdated: (event: RowDataUpdatedEvent) => this.setRowCountLabel(),
            onFilterChanged: () => {
                this.gridApi.redrawRows();
                this.setRowCountLabel();
            },
            getRowId: ({data: rowData}) => rowData.MetricID,
            getRowStyle: this.getRowStyle.bind(this),
            onRowSelected: this.onRowSelected.bind(this),
        };
    }

    getRowStyle(params: RowClassParams) {
        let isRowDisabled = null;
        this.inProgressMetricList.forEach(item => {
            const metricNames = item.split(',').map(def => def.trim());
            if (metricNames.includes(params.data.MetricName.trim()) || params.data.MetricID === this.selectedMetricId.toString()) {
                isRowDisabled =  { 'pointer-events': 'none', opacity: 0.5 };
            }
        });
        return isRowDisabled;
    }

    onFirstDataRendered(event: FirstDataRenderedEvent) {
        const rowNode = event.api.getRowNode(this.selectedMetricId.toString());
        if (rowNode) {
            rowNode.setSelected(true);
        }
    }

    onRowSelected(event: any) {
        let metricNames: string[];
        let skipLogic = false;
        this.inProgressMetricList.forEach(item => {
            metricNames = item.split(',').map(def => def.trim());
            if (metricNames.includes(event.data.MetricName.trim())) {
                skipLogic = true;
            }
        });
        if (!skipLogic) {
            if (event.data.MetricID === this.selectedMetricId.toString()) {
                event.node.setSelected(true);
                if (!this.selectedMetricDefList.includes(event.data.MetricID)) {
                    this.selectedMetricDefList.push(event.data.MetricID);
                }
            } else {
                if (event.data.MetricID !== this.selectedMetricId.toString() && event.node.isSelected()) {
                    this.selectedMetricDefList.push(event.data.MetricID);
                } else {
                    let index = this.selectedMetricDefList.indexOf(event.data.MetricID);
                    this.selectedMetricDefList.splice(index, 1);
                }
            }
        }
        this.isDefinitionSelected = this.selectedMetricDefList.length > 0;

        if ((this.selectedMetricDefList ? this.selectedMetricDefList.length : 0) +
            (this.inProgressMetricList ? this.inProgressMetricList.length : 0) <= this.entitiesMaxSelectionLimit) {
            this.isDefinitionSelected = true;
            this.showWarningMsg = false;
        } else {
            this.isDefinitionSelected = false;
            this.showWarningMsg = true;
        }
    }

    getReportingSetName(components: any): string {
        if (components && components.length > 0) {
            const reportingSetNames = components.map(component => component.ReportingSetName);
            return reportingSetNames.join(', ');
        }
        return '';
    }

    public async loadDataRecalculation(): Promise<void> {
        try {
            const response = await this.cxoneEtlRecalculation.loadRecalculationMetricData(false);
            const metricData = response
                .filter(inputObject => inputObject.Type === MetricTypes.Standard)
                .map((inputObject, index) => ({
                    _id: index,
                    MetricID: inputObject.MetricID.toString(),
                    MetricName: inputObject.MetricName,
                    Group: inputObject.Group,
                    Components: this.getReportingSetName(inputObject.Components)
                }));

            this.gridDataMetrics = metricData;
            this.setGridRecordData();
        } catch (error) {
            console.error('Failed to load metric data:', error);
            this.gridDataMetrics = [];
            this.setGridRecordData();
            this.toastrManager.showToaster('error', this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
    }

    public setGridRecordData() {
        this.gridApi.setRowData(this.gridDataMetrics);
        this.showSpinner = false;
        if (this.gridApi) {
            this.gridApi.onFilterChanged();
            this.manageNoRowsOverlay();
        }
        this.gridApi.sizeColumnsToFit();
    }

    public onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.loadDataRecalculation();
    }

    public onSearchCriteriaChange = (searchCriteria: string) => {
        this.searchInput = searchCriteria;
        this.gridApi.onFilterChanged();
        if (this.gridApi?.getDisplayedRowCount() === 0) {
            this.gridApi.showNoRowsOverlay();
        } else {
            this.gridApi?.hideOverlay();
        }
    };

    public manageNoRowsOverlay() {
        if (this.gridApi.getDisplayedRowCount() === 0)
            this.gridApi.showNoRowsOverlay();
        else
            this.gridApi.hideOverlay();
    }

    public setRowCountLabel() {
        let count = 0;
        this.gridApi.forEachNodeAfterFilter(() => {
            count++;
        });
        this.metricsLabel = this.getTotalRecalculationsText(count);
    }

    public getFilterText(): string {
        return this.searchInput.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    public getTotalRecalculationsText(count: number): string {
        const key: string = count === 1 ? 'Total_Metrics_Singular' : 'Total_Metrics_Plural';
        const defaultValue: string = count === 1 ? '{0} metric' : '{0} metrics';
        return this.cvLocalizationService.Format(this.getLocalizationString(key, defaultValue), count);
    }

    cancel() {
        this.dialogRef.close();
    }

    save() {
        this.saveRecalculationData();
    }

    public async saveRecalculationData() {
        if (this.isActionRunning)
            return;
        else
            this.isActionRunning = true;

        const recalculationData: SaveRecalculationDataObj = {
            startDate: this.startDate,
            endDate: this.endDate,
            metricIds: this.selectedMetricDefList ? this.selectedMetricDefList : [],
            reportingSetIds: [],
            hierarchyIds: []
        };
        try {
            await this.cxoneEtlRecalculation.saveRecalculationData(recalculationData);
            this.dialogRef.close(recalculationData);
            if (this.isRecalculationContextOption || this.hideMetricsUpdatedToaster)
                this.toastrManager.showToaster('success', this.getLocalizationString('Recalculation_InProgress', 'Recalculation is in progress...'));
            else {
                this.toastrManager.showToaster('success', this.getLocalizationString('Recalculation_InProgress', 'Recalculation is in progress...'));
                this.toastrManager.showToaster('success', this.getLocalizationString('Metric_Updated', 'Metric updated successfully.'));
            }
        } catch (error) {
            this.toastrManager.showToaster('error', this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
            this.dialogRef.close();
        } finally {
            this.searchInput = '';
        }

        this.isActionRunning = false;
    }

    onDateRangeChanged(selectedDate: any) {
        selectedDate.fromDate = new Date(selectedDate.fromDate);
        selectedDate.fromDate.setDate(1);
        selectedDate.toDate = new Date(selectedDate.toDate.getFullYear(), selectedDate.toDate.getMonth() + 1, 0);
        const startDateObject = new Date(selectedDate.fromDate);
        const endDateObject = new Date(selectedDate.toDate);
        this.startDate = this.datePipe.transform(startDateObject, 'yyyy-MM-dd');
        this.endDate = this.datePipe.transform(endDateObject, 'yyyy-MM-dd');
    }

    setSelectableDateRange() {
        const currentDate = new Date();
        const maxDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        this.customDateRange = { maxDate };
    }
}
